/* Voice-to-text for the prompt textarea, wrapping the browser's built-in
 * SpeechRecognition. Click the mic to start dictation; click again to
 * stop. Recognized text lands at the current cursor position (or gets
 * appended when there's no cursor selection), so you can dictate over
 * existing typing without wiping it.
 *
 * Browser support (Sep 2026):
 *   - Chrome / Edge / Opera (desktop + Android)  ✓  via `webkitSpeechRecognition`
 *   - Safari 14+ (desktop + iOS)                 ✓  via `SpeechRecognition`
 *   - Firefox                                    ✗  no ship of Web Speech API
 *
 * The recognition language mirrors the current i18n language: EN → en-US,
 * ES → es-ES (with es-MX fallback), PT → pt-BR. That way the user doesn't
 * have to think about it — switch UI language, dictation follows.
 *
 * Firefox users see a friendly toast telling them to use Chrome/Edge/Safari.
 * The mic button stays visible either way so the affordance is discoverable
 * even when it can't run — clicking on Firefox surfaces the message once.
 */

import { toast } from './toast';
import { t, getCurrentLang } from './i18n';

type RecognitionState = 'idle' | 'listening';

interface SRLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onstart: ((e: any) => void) | null;
  onend: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onresult: ((e: any) => void) | null;
  start: () => void;
  stop: () => void;
}

function getSpeechRecognitionCtor(): (new () => SRLike) | null {
  const w = window as any;
  return (w.SpeechRecognition || w.webkitSpeechRecognition) || null;
}

function langCodeForRecognition(): string {
  const l = getCurrentLang() || 'en';
  if (l.startsWith('es')) return 'es-ES';
  if (l.startsWith('pt')) return 'pt-BR';
  return 'en-US';
}

// ---- Insert text at cursor (preserves what the user already typed) --------

function insertAtCursor(ta: HTMLTextAreaElement, text: string): void {
  if (!text) return;
  const start = ta.selectionStart ?? ta.value.length;
  const end   = ta.selectionEnd   ?? ta.value.length;
  const before = ta.value.slice(0, start);
  const after  = ta.value.slice(end);
  // Add a space between two words if the caret is right after a non-space
  // and the recognized text doesn't start with one.
  const sep = before.length && !/\s$/.test(before) && !/^\s/.test(text) ? ' ' : '';
  ta.value = before + sep + text + after;
  const caret = (before + sep + text).length;
  ta.selectionStart = ta.selectionEnd = caret;
  // Let the run-button / char-count listeners react.
  ta.dispatchEvent(new Event('input', { bubbles: true }));
}

// ---- Wiring ---------------------------------------------------------------

let _rec: SRLike | null = null;
let _state: RecognitionState = 'idle';
let _interimBuffer = '';   // interim results from the current utterance
let _interimStart = -1;    // caret pos where the interim block starts
let _wasSupportedToastShown = false;

function setButtonState(btn: HTMLButtonElement, s: RecognitionState): void {
  _state = s;
  btn.classList.toggle('mic-active', s === 'listening');
  btn.title = s === 'listening'
    ? (t('mic.tooltip_stop') || 'Stop dictation')
    : (t('mic.tooltip')      || 'Dictate the prompt (Chrome/Edge/Safari)');
  const label = btn.querySelector('[data-i18n]') as HTMLElement | null;
  if (label) {
    label.textContent = s === 'listening'
      ? (t('mic.button_stop') || 'Stop')
      : (t('mic.button')      || 'Dictate');
  }
}

function stopRecognition(): void {
  if (_rec) {
    try { _rec.stop(); } catch { /* fine, may already be stopped */ }
  }
}

function startRecognition(btn: HTMLButtonElement, ta: HTMLTextAreaElement): void {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    if (!_wasSupportedToastShown) {
      toast(t('mic.unsupported') || 'Voice dictation needs Chrome, Edge, or Safari.');
      _wasSupportedToastShown = true;
    }
    return;
  }
  const rec = new Ctor();
  rec.lang = langCodeForRecognition();
  rec.interimResults = true;
  rec.continuous = true;

  _interimBuffer = '';
  _interimStart = -1;

  rec.onstart = () => setButtonState(btn, 'listening');

  rec.onresult = (ev: any) => {
    // Walk results starting at resultIndex — the browser tells us which
    // ones are new / updated this event, so we don't double-append.
    let finalChunk = '';
    let interimChunk = '';
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const r = ev.results[i];
      const txt = r[0]?.transcript || '';
      if (r.isFinal) finalChunk += txt;
      else           interimChunk += txt;
    }

    // First, strip whatever interim text we last painted so we don't keep
    // stacking speculative transcripts.
    if (_interimStart >= 0 && _interimBuffer) {
      const before = ta.value.slice(0, _interimStart);
      const after  = ta.value.slice(_interimStart + _interimBuffer.length);
      ta.value = before + after;
    }

    // Insert the final chunk (if any) at the interim anchor, then paint
    // fresh interim after it.
    if (finalChunk) {
      // Reset the caret to the interim anchor before inserting final text.
      const anchor = _interimStart >= 0 ? _interimStart : (ta.selectionStart ?? ta.value.length);
      ta.selectionStart = ta.selectionEnd = anchor;
      insertAtCursor(ta, finalChunk);
      _interimStart = ta.selectionStart!;
      _interimBuffer = '';
    } else if (_interimStart < 0) {
      _interimStart = ta.selectionStart ?? ta.value.length;
    }

    if (interimChunk) {
      const before = ta.value.slice(0, _interimStart);
      const after  = ta.value.slice(_interimStart);
      const sep = before.length && !/\s$/.test(before) ? ' ' : '';
      const paint = sep + interimChunk;
      ta.value = before + paint + after;
      _interimBuffer = paint;
      ta.selectionStart = ta.selectionEnd = _interimStart + paint.length;
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  rec.onerror = (ev: any) => {
    const err = String(ev?.error || '');
    if (err === 'no-speech' || err === 'aborted') return; // benign
    if (err === 'not-allowed' || err === 'service-not-allowed') {
      toast(t('mic.permission_denied') || 'Microphone permission denied. Enable it in the browser and retry.');
    } else if (err === 'network') {
      toast(t('mic.network') || 'Voice recognition network error. Check your connection and retry.');
    } else if (err) {
      toast((t('mic.error') || 'Voice recognition failed') + ': ' + err);
    }
  };

  rec.onend = () => {
    // Commit any interim text as-is (browser stopped without final result).
    _interimBuffer = '';
    _interimStart = -1;
    _rec = null;
    setButtonState(btn, 'idle');
  };

  try {
    rec.start();
    _rec = rec;
  } catch (err: any) {
    // start() throws if already started elsewhere; surface a hint.
    console.warn('SpeechRecognition.start() failed', err);
    toast((t('mic.error') || 'Voice recognition failed') + ': ' + (err?.message || err));
  }
}

export function attachVoiceInputHandlers(): void {
  const btn = document.getElementById('mic-btn') as HTMLButtonElement | null;
  const ta  = document.getElementById('prompt-input') as HTMLTextAreaElement | null;
  if (!btn || !ta) return;

  // Hide the button entirely on browsers that lack the API to keep the UI
  // honest. (Firefox is the only common holdout as of Sep 2026.)
  if (!getSpeechRecognitionCtor()) {
    btn.style.display = 'none';
    return;
  }

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (_state === 'listening') stopRecognition();
    else                        startRecognition(btn, ta);
  });

  // If the user switches i18n language mid-session, the next dictation
  // uses the new locale automatically because we read getCurrentLang() at
  // start-time — nothing to do here.
}
